import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Building2, Home, Store, MapPin, TrendingUp, 
  CheckCircle2, ChevronRight, Filter, Grid3X3, List,
  DollarSign, Users, Calendar, BarChart3, Target, Award
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Portfolio properties data
  const properties = [
    {
      id: 1,
      name: 'Jarmese Apartments',
      type: 'multifamily',
      status: 'active',
      location: 'Houston, TX',
      submarket: 'Greater Houston',
      units: 156,
      sqft: '142,000',
      year: 2023,
      strategy: 'Value-Add',
      description: 'Class B multifamily community undergoing comprehensive renovation program to capture rent premiums in growing submarket.',
      highlights: ['Unit Renovations', 'Amenity Upgrades', 'Operational Improvements'],
      image: 'https://customer-assets.emergentagent.com/job_asapequity/artifacts/08whp4id_fa144646-1695-402a-989f-059766089009.png',
      caseStudyUrl: '/case-studies/jarmese-apartments'
    },
    {
      id: 2,
      name: 'Northgreen Atrium Building',
      type: 'office',
      status: 'active',
      location: 'Houston, TX',
      submarket: 'Greenspoint',
      units: null,
      sqft: '85,000',
      year: 2024,
      strategy: 'Value-Add',
      description: 'Class B office building with strong tenant base, positioned for lease-up and NOI optimization.',
      highlights: ['Tenant Retention', 'Common Area Updates', 'Energy Efficiency'],
      image: 'https://customer-assets.emergentagent.com/job_asapequity/artifacts/cv95j4qz_f009021d-4a44-4152-8f21-08346032a255.png',
      caseStudyUrl: '/case-studies/northgreen-atrium'
    },
    {
      id: 3,
      name: 'Griggs Shopping Center',
      type: 'retail',
      status: 'active',
      location: 'Houston, TX',
      submarket: 'Southeast Houston',
      units: null,
      sqft: '52,000',
      year: 2023,
      strategy: 'Value-Add',
      description: 'Neighborhood retail center with strong anchor tenants, executing lease-up strategy for vacant inline spaces.',
      highlights: ['Anchor Tenants', 'Lease-Up Program', 'Facade Improvements'],
      image: 'https://customer-assets.emergentagent.com/job_asapequity/artifacts/fgrcxat0_e566c84b-016a-486b-87c5-d8862f36af0d.png',
      caseStudyUrl: '/case-studies/griggs-shopping-center'
    },
    {
      id: 4,
      name: 'Hollyvale Apartments',
      type: 'multifamily',
      status: 'realized',
      location: 'Houston, TX',
      submarket: 'Southwest Houston',
      units: 212,
      sqft: '198,000',
      year: 2019,
      exitYear: 2023,
      strategy: 'Value-Add',
      returns: { irr: '24.5%', equity: '2.1x' },
      description: 'Successfully executed value-add business plan with full renovation and repositioning, achieving above-target returns.',
      highlights: ['Full Renovation', 'Rent Growth', 'Successful Exit'],
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/kexllnmx_490aeb1c-f827-4484-9025-f487279765b7.png',
      caseStudyUrl: '/case-studies/hollyvale-apartments'
    }
  ];

  // Filter properties
  const filteredProperties = activeFilter === 'all' 
    ? properties 
    : properties.filter(p => p.type === activeFilter || p.status === activeFilter);

  // Portfolio stats
  const portfolioStats = [
    { label: 'Total Assets', value: '4', icon: Building2 },
    { label: 'Square Feet', value: '477K+', icon: Grid3X3 },
    { label: 'Multifamily Units', value: '368', icon: Home },
    { label: 'Markets', value: '4', icon: MapPin }
  ];

  // Geographic distribution
  const submarkets = [
    { name: 'Greater Houston', count: 2, type: 'Primary' },
    { name: 'Southwest Houston', count: 1, type: 'Primary' },
    { name: 'Southeast Houston', count: 1, type: 'Secondary' },
    { name: 'Greenspoint', count: 1, type: 'Emerging' }
  ];

  // Filter options
  const filters = [
    { id: 'all', label: 'All Properties' },
    { id: 'active', label: 'Active' },
    { id: 'realized', label: 'Realized' },
    { id: 'multifamily', label: 'Multifamily' },
    { id: 'retail', label: 'Retail' },
    { id: 'office', label: 'Office' }
  ];

  const getTypeIcon = (type) => {
    switch(type) {
      case 'multifamily': return Home;
      case 'retail': return Store;
      case 'office': return Building2;
      default: return Building2;
    }
  };

  return (
    <>
      <Helmet>
        <title>Houston Real Estate Investment Portfolio | ASAP Capital</title>
        <meta 
          name="description" 
          content="View ASAP Capital's portfolio of multifamily, retail, and office real estate investments across Houston and key Texas markets." 
        />
        <meta name="keywords" content="Houston real estate investment portfolio, multifamily properties Houston, commercial properties Houston, private equity real estate assets" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4AF37]/3 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl"
              >
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-8"
                  variants={itemVariants}
                >
                  Our Houston{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Real Estate
                  </span>{' '}
                  Investment Portfolio
                </motion.h1>

                <motion.p 
                  className="text-base lg:text-lg text-white/60 leading-relaxed mb-10 max-w-2xl"
                  variants={itemVariants}
                >
                  Explore our diversified portfolio of value-add multifamily, retail, and office properties 
                  across Greater Houston and key Texas markets.
                </motion.p>

                {/* Portfolio Stats */}
                <motion.div 
                  className="grid grid-cols-2 lg:grid-cols-4 gap-4"
                  variants={itemVariants}
                >
                  {portfolioStats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      className="bg-[#111D35]/60 backdrop-blur-sm border border-white/10 rounded-xl p-4 group hover:border-[#D4AF37]/30 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      whileHover={{ y: -4 }}
                    >
                      <stat.icon className="h-5 w-5 text-[#D4AF37] mb-2" />
                      <div className="text-2xl lg:text-3xl font-bold text-white">{stat.value}</div>
                      <p className="text-white/50 text-xs">{stat.label}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ACTIVE INVESTMENTS ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                      variants={itemVariants}
                    >
                      Current Holdings
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-4"
                      variants={itemVariants}
                    >
                      Active Investments
                    </motion.h2>
                    <motion.p 
                      className="text-white/50 max-w-xl"
                      variants={itemVariants}
                    >
                      Value-add properties currently under management across Greater Houston, 
                      executing operational improvements and long-term income strategies.
                    </motion.p>
                  </div>

                  {/* Filter Controls */}
                  <motion.div 
                    className="flex flex-wrap gap-2"
                    variants={itemVariants}
                  >
                    {filters.map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => setActiveFilter(filter.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                          activeFilter === filter.id
                            ? 'bg-[#D4AF37] text-[#0A1628]'
                            : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </motion.div>
                </div>

                {/* Properties Grid */}
                <motion.div 
                  className="grid md:grid-cols-2 gap-6"
                  layout
                >
                  <AnimatePresence mode="popLayout">
                    {filteredProperties.filter(p => p.status === 'active').map((property, index) => (
                      <motion.div
                        key={property.id}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl overflow-hidden border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group"
                        whileHover={{ y: -8, boxShadow: '0 25px 60px rgba(0,0,0,0.4)' }}
                      >
                        {/* Property Image */}
                        <div className="relative h-56 overflow-hidden">
                          <img 
                            src={property.image}
                            alt={property.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-transparent" />
                          
                          {/* Status Badge */}
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium border border-green-500/30">
                              Active
                            </span>
                          </div>
                          
                          {/* Type Badge */}
                          <div className="absolute top-4 right-4">
                            <span className="px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium capitalize">
                              {property.type}
                            </span>
                          </div>
                        </div>
                        
                        {/* Property Content */}
                        <div className="p-6">
                          <div className="flex items-center gap-2 text-white/40 text-sm mb-2">
                            <MapPin className="h-4 w-4" />
                            {property.location} • {property.submarket}
                          </div>
                          
                          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                            {property.name}
                          </h3>
                          
                          <p className="text-white/50 text-sm leading-relaxed mb-4">
                            {property.description}
                          </p>
                          
                          {/* Property Stats */}
                          <div className="flex flex-wrap gap-4 mb-4 text-sm">
                            {property.units && (
                              <div className="flex items-center gap-1 text-white/60">
                                <Home className="h-4 w-4 text-[#D4AF37]" />
                                {property.units} Units
                              </div>
                            )}
                            <div className="flex items-center gap-1 text-white/60">
                              <Grid3X3 className="h-4 w-4 text-[#D4AF37]" />
                              {property.sqft} SF
                            </div>
                            <div className="flex items-center gap-1 text-white/60">
                              <Calendar className="h-4 w-4 text-[#D4AF37]" />
                              {property.year}
                            </div>
                          </div>
                          
                          {/* Highlights */}
                          <div className="flex flex-wrap gap-2 mb-5">
                            {property.highlights.map((highlight, i) => (
                              <span 
                                key={i}
                                className="px-2 py-1 rounded bg-white/5 text-white/50 text-xs border border-white/10"
                              >
                                {highlight}
                              </span>
                            ))}
                          </div>
                          
                          {/* CTA */}
                          <Link to={property.caseStudyUrl}>
                            <Button 
                              variant="outline" 
                              className="w-full border-white/20 text-white hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/30 rounded-full text-sm"
                            >
                              View Case Study
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== REALIZED INVESTMENTS ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Track Record
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Realized Investments
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-xl"
                    variants={itemVariants}
                  >
                    Successfully executed investments demonstrating our value-add capabilities and return generation.
                  </motion.p>
                </div>

                {/* Realized Properties */}
                {filteredProperties.filter(p => p.status === 'realized').map((property, index) => (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl overflow-hidden border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group"
                  >
                    <div className="grid lg:grid-cols-2">
                      {/* Image */}
                      <div className="relative h-64 lg:h-auto overflow-hidden">
                        <img 
                          src={property.image}
                          alt={property.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A1628]/80 lg:block hidden" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] to-transparent lg:hidden" />
                        
                        {/* Badges */}
                        <div className="absolute top-4 left-4 flex gap-2">
                          <span className="px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium border border-[#D4AF37]/30">
                            Realized
                          </span>
                          <span className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs font-medium capitalize">
                            {property.type}
                          </span>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-6 lg:p-8">
                        <div className="flex items-center gap-2 text-white/40 text-sm mb-2">
                          <MapPin className="h-4 w-4" />
                          {property.location} • {property.submarket}
                        </div>
                        
                        <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                          {property.name}
                        </h3>
                        
                        <p className="text-white/50 text-sm leading-relaxed mb-6">
                          {property.description}
                        </p>
                        
                        {/* Returns */}
                        {property.returns && (
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-[#D4AF37]/10 rounded-xl p-4 border border-[#D4AF37]/20">
                              <p className="text-white/50 text-xs mb-1">Project IRR</p>
                              <p className="text-2xl font-bold text-[#D4AF37]">{property.returns.irr}</p>
                            </div>
                            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                              <p className="text-white/50 text-xs mb-1">Equity Multiple</p>
                              <p className="text-2xl font-bold text-white">{property.returns.equity}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Timeline */}
                        <div className="flex items-center gap-4 text-sm text-white/60 mb-6">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 text-[#D4AF37]" />
                            Acquired: {property.year}
                          </div>
                          <div className="flex items-center gap-1">
                            <TrendingUp className="h-4 w-4 text-[#D4AF37]" />
                            Exit: {property.exitYear}
                          </div>
                        </div>
                        
                        {/* CTA */}
                        <div className="flex gap-3">
                          <Link to={property.caseStudyUrl} className="flex-1">
                            <Button className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full text-sm">
                              View Case Study
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </Link>
                          <Link to="/track-record">
                            <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full text-sm">
                              Track Record
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ==================== GEOGRAPHIC FOCUS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                  {/* Left Column - Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Market Focus
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Geographic Distribution
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-10"
                      variants={itemVariants}
                    >
                      Our portfolio spans key Houston submarkets, targeting high-growth corridors 
                      with strong employment drivers and demographic tailwinds.
                    </motion.p>
                    
                    {/* Submarkets List - Fixed spacing */}
                    <motion.div className="space-y-4" variants={itemVariants}>
                      {submarkets.map((submarket, index) => (
                        <motion.div
                          key={submarket.name}
                          className="flex items-center justify-between p-4 bg-[#111D35]/60 rounded-xl border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 5, backgroundColor: 'rgba(17, 29, 53, 0.8)' }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                              <MapPin className="h-4 w-4 text-[#D4AF37]" />
                            </div>
                            <span className="text-white font-medium group-hover:text-[#D4AF37] transition-colors">
                              {submarket.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              submarket.type === 'Primary' 
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' 
                                : submarket.type === 'Secondary'
                                ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                                : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                            }`}>
                              {submarket.type}
                            </span>
                            <span className="px-2.5 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium border border-[#D4AF37]/20 min-w-[70px] text-center">
                              {submarket.count} {submarket.count === 1 ? 'Asset' : 'Assets'}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Right Column - Info Card - Stretch to match left */}
                  <motion.div 
                    className="flex"
                    variants={itemVariants}
                  >
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 relative overflow-hidden w-full flex flex-col">
                      {/* Grid pattern background */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0" style={{
                          backgroundImage: `radial-gradient(circle at 2px 2px, #D4AF37 1px, transparent 0)`,
                          backgroundSize: '24px 24px'
                        }} />
                      </div>
                      
                      {/* Decorative glow */}
                      <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
                      
                      <div className="relative z-10 text-center flex-1 flex flex-col justify-center">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-5 border border-[#D4AF37]/20">
                          <MapPin className="h-8 w-8 text-[#D4AF37]" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Greater Houston Metro</h3>
                        <p className="text-white/50 text-sm mb-8">
                          7.5M+ Population • 4th Largest US Metro
                        </p>
                        
                        {/* Asset Type Breakdown - Fixed grid */}
                        <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/10 mt-auto">
                          <div className="text-center">
                            <div className="w-12 h-12 mx-auto rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-3">
                              <Home className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <p className="text-xl font-bold text-white">2</p>
                            <p className="text-white/40 text-xs mt-1">Multifamily</p>
                          </div>
                          <div className="text-center">
                            <div className="w-12 h-12 mx-auto rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-3">
                              <Store className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <p className="text-xl font-bold text-white">1</p>
                            <p className="text-white/40 text-xs mt-1">Retail</p>
                          </div>
                          <div className="text-center">
                            <div className="w-12 h-12 mx-auto rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-3">
                              <Building2 className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <p className="text-xl font-bold text-white">1</p>
                            <p className="text-white/40 text-xs mt-1">Office</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CASE STUDIES CTA ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accent */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                      variants={itemVariants}
                    >
                      Deep Dive
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-4"
                      variants={itemVariants}
                    >
                      Investment Case Studies
                    </motion.h2>
                    <motion.p 
                      className="text-white/60 leading-relaxed"
                      variants={itemVariants}
                    >
                      Explore detailed analyses of our investment strategies, execution plans, 
                      and performance metrics for each property in our portfolio.
                    </motion.p>
                  </div>
                  
                  <motion.div 
                    className="flex flex-wrap gap-4 lg:justify-end"
                    variants={itemVariants}
                  >
                    <Link to="/case-studies">
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2">
                        View All Case Studies
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/track-record">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                        Track Record
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FINAL CTA ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="text-center"
              >
                <motion.h2 
                  className="text-2xl lg:text-3xl font-bold text-white mb-4"
                  variants={itemVariants}
                >
                  Explore Investment Opportunities
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-xl mx-auto mb-8"
                  variants={itemVariants}
                >
                  Learn how you can participate in our Houston real estate investment portfolio 
                  alongside experienced operators.
                </motion.p>
                
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link 
                    to="/contact"
                    
                    
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2">
                      Schedule Consultation
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/investment-process">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2">
                      Investment Process
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PortfolioPage;
